package com.exura.pages.planCreator.components.seriesViews

import androidx.compose.runtime.*
import com.exura.features.plandefinition.domain.builder.series.SimpleFloatingSeriesBuilderNew
import com.exura.features.plandefinition.domain.model.plan.PlanType
import com.exura.features.plandefinition.domain.model.series.HourOfDay
import com.exura.features.plandefinition.domain.model.series.SeriesType
import com.exura.pages.planCreator.components.ViewMode
import com.exura.pages.planCreator.components.seriesViews.inputs.PlanCreatorInputMode
import com.exura.pages.planCreator.components.seriesViews.sections.*
import com.exura.pages.planCreator.model.SeriesTypeRegistry
import com.exura.ui.Dimens
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import org.jetbrains.compose.web.css.px

/**
 * Enhanced Simple Floating Series editor view that works with SimpleFloatingSeriesBuilderNew
 * Provides real-time editing with change tracking and full progressive disclosure flow
 */
@Composable
internal fun SimpleFloatingSeriesView(
    builder: SimpleFloatingSeriesBuilderNew,
    isNewSeries: Boolean = true,
    viewMode: ViewMode,
    planType: PlanType,
    onAction: () -> Unit,
) {
    val typeInfo = SeriesTypeRegistry.getTypeInfo(SeriesType.SIMPLE_FLOATING)!!
    val inputMode = if (viewMode == ViewMode.EDIT) PlanCreatorInputMode.EDIT else PlanCreatorInputMode.VIEW

    // Default view values - backing state variables
    var name by remember(builder) { mutableStateOf("") }
    var description by remember(builder) { mutableStateOf("") }
    var instruction by remember(builder) { mutableStateOf("") }
    var durationInMinutes by remember(builder) { mutableStateOf(5) }
    var deadline by remember(builder) { mutableStateOf(0) }
    var exactTime by remember(builder) { mutableStateOf<HourOfDay?>(null) }

    // Mode display values
    var deadlineMode by remember(builder) { mutableStateOf(RelativeDayMode.ON_VISIT_DAY) }
    var hasExecutionDeadline by remember(builder) { mutableStateOf(false) }
    var deadlineDisplay by remember(builder) { mutableStateOf(1) }

    // Change tracking
    var hasChanges by remember(builder) { mutableStateOf(false) }
    var isValid by remember(builder) { mutableStateOf(false) }

    // Computed derived state
    val seriesHasName by remember(name) { mutableStateOf(name.isNotBlank()) }
    val hasMainElement by remember(instruction) { mutableStateOf(instruction.isNotBlank()) }

    fun updateChanges() {
        hasChanges = builder.isChanged
        val validationMessages = builder.validate()
        isValid = validationMessages.isEmpty()
    }

    // Setting on launch/change - initialize from builder state
    LaunchedEffect(builder) {
        name = builder.name() ?: name
        description = builder.description() ?: description
        instruction = builder.instruction() ?: instruction
        durationInMinutes = builder.durationInMinutes() ?: durationInMinutes
        deadline = builder.deadline() ?: deadline
        exactTime = builder.exactTime() ?: exactTime

        // Set mode display values based on stored values
        deadlineMode = if (deadline == 0) RelativeDayMode.ON_VISIT_DAY else RelativeDayMode.DAYS_OFFSET
        hasExecutionDeadline = builder.deadline() != null || exactTime != null
        deadlineDisplay = when (planType) {
            PlanType.PREPLAN -> kotlin.math.abs(deadline).coerceAtLeast(1)
            PlanType.POSTPLAN -> deadline.coerceAtLeast(1)
        }

        // Apply initial values to builder
        builder.setName(name)
        builder.setDescription(description)
        builder.setInstruction(instruction)
        builder.setDurationInMinutes(durationInMinutes)
        builder.setDeadline(if (hasExecutionDeadline) deadline else null)
        builder.setExactTime(exactTime)

//        builder.markSaved()
    }

    SeriesView(
        hasChanges = hasChanges,
        isValid = isValid,
        typeInfo = typeInfo,
        isNewSeries = isNewSeries,
        viewMode = inputMode,
        onAction = {
            onAction()
            builder.markSaved()
            updateChanges()
        },
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(32.px)
                .overflow(Overflow.Auto),
            verticalArrangement = Arrangement.spacedBy(0.px)
        ) {

            NameSeriesSection(
                value = name,
                onValueChange = { new ->
                    name = new
                    builder.setName(new)
                    updateChanges()
                },
                mode = inputMode,
            )

            DescriptionSeriesSection(
                value = description,
                onValueChange = { new ->
                    description = new
                    builder.setDescription(new)
                    updateChanges()
                },
                mode = inputMode,
                placeholder = "Opisz co ma się wydarzyć...",
                enabled = seriesHasName,
            )

            EstimatedMinutesSection(
                estimatedMinutes = durationInMinutes,
                onEstimatedMinutesChange = { new ->
                    durationInMinutes = new
                    builder.setDurationInMinutes(new)
                    updateChanges()
                },
                mode = inputMode,
                enabled = seriesHasName,
            )

            ElementMainNameSection(
                value = instruction,
                onValueChange = { new ->
                    instruction = new
                    builder.setInstruction(new)
                    updateChanges()
                },
                mode = inputMode,
                title = "Instrukcja",
                placeholder = "np. Dostarcz badania krwi...",
                enabled = seriesHasName,
            )

            ExecutionDeadlineToggle(
                hasExecutionDeadline = hasExecutionDeadline,
                onToggle = { enabled ->
                    hasExecutionDeadline = enabled
                    if (!enabled) {
                        builder.setDeadline(null)
                        builder.setExactTime(null)
                    } else {
                        builder.setDeadline(deadline)
                        builder.setExactTime(exactTime)
                    }
                    updateChanges()
                },
                mode = inputMode,
                enabled = seriesHasName && hasMainElement,
            )

            RelativeDaySection(
                selectedValue = deadlineMode,
                relativeDay = deadlineDisplay,
                onRelativeDayChange = { new ->
                    deadlineMode = RelativeDayMode.DAYS_OFFSET
                    deadlineDisplay = new
                    deadline = when (planType) {
                        PlanType.PREPLAN -> -new
                        PlanType.POSTPLAN -> new
                    }
                    builder.setDeadline(deadline)
                    updateChanges()
                },
                onModeChange = { newMode ->
                    deadlineMode = newMode
                    when(newMode) {
                        RelativeDayMode.NONE -> {}
                        RelativeDayMode.ON_VISIT_DAY -> {
                            builder.setDeadline(0)
                            deadline = 0
                        }
                        RelativeDayMode.DAYS_OFFSET -> {
                            deadline = when (planType) {
                                PlanType.PREPLAN -> -deadlineDisplay
                                PlanType.POSTPLAN -> deadlineDisplay
                            }
                            builder.setDeadline(deadline)
                        }
                    }
                    updateChanges()
                },
                mode = inputMode,
                enabled = seriesHasName && hasMainElement && hasExecutionDeadline,
                planType = planType,
            )

            TimeOfDaySection(
                selectedValue = exactTime,
                onValueChange = { newTime ->
                    exactTime = newTime
                    builder.setExactTime(newTime)
                    updateChanges()
                },
                mode = inputMode,
                enabled = seriesHasName && hasMainElement && hasExecutionDeadline,
                title = "Dokładny czas"
            )

        }
    }
}
